import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useLoginMutation, useRegisterMutation } from "../../app/config/redux/slices/api/userApiSlice";
import { setCredentials } from "../../app/config/redux/slices/authSlice";

const CheckoutAuthModal = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState("login");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [registerMobile, setRegisterMobile] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [login, { isLoading: isLoggingIn }] = useLoginMutation();
  const [register, { isLoading: isRegistering }] = useRegisterMutation();

  useEffect(() => {
    const closeOnEscape = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  const completeLogin = async (loginMobile, loginPassword) => {
    const response = await login({ mobile: loginMobile, password: loginPassword }).unwrap();
    dispatch(setCredentials({
      id: response.user.id,
      username: response.user.username,
      name: response.user.name,
      access_token: response.access_token,
    }));
    onClose();
    navigate("/checkout");
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setMessage("");
    try {
      await completeLogin(mobile, password);
    } catch (error) {
      setMessage(error?.data?.message || "We couldn't sign you in. Please check your details and try again.");
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setMessage("");
    if (registerPassword !== confirmPassword) {
      setMessage("Passwords do not match. Please try again.");
      return;
    }
    try {
      await register({ name, mobile: registerMobile, password: registerPassword }).unwrap();
      await completeLogin(registerMobile, registerPassword);
    } catch (error) {
      setMessage(error?.data?.message || "We couldn't create your account. Please try again.");
    }
  };

  return (
    <div className="checkout-auth-modal" role="dialog" aria-modal="true" aria-labelledby="checkout-auth-title">
      <button className="checkout-auth-modal__backdrop" type="button" aria-label="Close sign in dialog" onClick={onClose} />
      <div className="checkout-auth-modal__panel">
        <button className="checkout-auth-modal__close" type="button" aria-label="Close" onClick={onClose}>
          <i className="ph ph-x" />
        </button>
        <div className="checkout-auth-modal__eyebrow">Secure checkout</div>
        <h2 id="checkout-auth-title">Almost there</h2>
        <p className="checkout-auth-modal__intro">Sign in or create an account to complete your order. Your cart is saved and waiting for you.</p>

        <div className="checkout-auth-modal__tabs" role="tablist" aria-label="Account access">
          <button type="button" role="tab" aria-selected={activeTab === "login"} className={activeTab === "login" ? "active" : ""} onClick={() => setActiveTab("login")}>Sign in</button>
          <button type="button" role="tab" aria-selected={activeTab === "register"} className={activeTab === "register" ? "active" : ""} onClick={() => setActiveTab("register")}>Create account</button>
        </div>

        {message && <div className="checkout-auth-modal__message" role="alert">{message}</div>}

        {activeTab === "login" ? (
          <form className="checkout-auth-modal__form" onSubmit={handleLogin}>
            <label htmlFor="checkout-login-mobile">Mobile number</label>
            <input id="checkout-login-mobile" className="common-input" type="tel" inputMode="numeric" placeholder="Enter your mobile number" value={mobile} onChange={(event) => setMobile(event.target.value)} required />
            <label htmlFor="checkout-login-password">Password</label>
            <input id="checkout-login-password" className="common-input" type="password" placeholder="Enter your password" value={password} onChange={(event) => setPassword(event.target.value)} required />
            <button className="btn btn-main py-18 rounded-8 w-100" type="submit" disabled={isLoggingIn}>{isLoggingIn ? "Signing in…" : "Continue to checkout"}</button>
            <Link className="checkout-auth-modal__forgot-password" to="/password" onClick={onClose}>Forgot password?</Link>
          </form>
        ) : (
          <form className="checkout-auth-modal__form" onSubmit={handleRegister}>
            <label htmlFor="checkout-register-name">Your name</label>
            <input id="checkout-register-name" className="common-input" type="text" placeholder="Enter your full name" value={name} onChange={(event) => setName(event.target.value)} required />
            <label htmlFor="checkout-register-mobile">Mobile number</label>
            <input id="checkout-register-mobile" className="common-input" type="tel" inputMode="numeric" placeholder="Enter your mobile number" value={registerMobile} onChange={(event) => setRegisterMobile(event.target.value)} required />
            <label htmlFor="checkout-register-password">Password</label>
            <input id="checkout-register-password" className="common-input" type="password" placeholder="Create a password" value={registerPassword} onChange={(event) => setRegisterPassword(event.target.value)} required />
            <label htmlFor="checkout-register-confirm-password">Confirm password</label>
            <input id="checkout-register-confirm-password" className="common-input" type="password" placeholder="Enter your password again" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required />
            <button className="btn btn-main py-18 rounded-8 w-100" type="submit" disabled={isRegistering}>{isRegistering ? "Creating account…" : "Create account & continue"}</button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CheckoutAuthModal;
